// ENG-2799: Redux slice for vacancy tracker action notifications.
// Mirrors FileManagerAlertsSlice — persistent alerts with progress-bar countdown.
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import type { ReactNode } from 'react';

export type NotificationSeverity = 'info';

export interface VacancyNotification {
  id: string;
  timeout: number;
  createdAt: number;
  severity: NotificationSeverity;
  message: ReactNode;
  read: boolean;
  completed: boolean;
  totalElapsedTime: number;
}

interface VacancyNotificationAlertsState {
  alerts: VacancyNotification[];
}

const initialState: VacancyNotificationAlertsState = {
  alerts: [],
};

const vacancyNotificationAlertsSlice = createSlice({
  name: 'vacancyNotificationAlerts',
  initialState,
  reducers: {
    addAlert: (
      state,
      action: PayloadAction<{
        message: ReactNode;
        timeout?: number;
      }>
    ) => {
      const alert: VacancyNotification = {
        id: uuidv4(),
        message: action.payload.message,
        timeout: action.payload.timeout || 15000,
        createdAt: Date.now(),
        severity: 'info',
        read: false,
        completed: false,
        totalElapsedTime: 0,
      };
      state.alerts.push(alert);
    },
    removeAlert: (state, action: PayloadAction<string>) => {
      state.alerts = state.alerts.filter((a) => a.id !== action.payload);
    },
    markAlertAsRead: (state, action: PayloadAction<string>) => {
      const alert = state.alerts.find((a) => a.id === action.payload);
      if (alert) {
        alert.read = true;
      }
    },
    markAlertAsCompleted: (state, action: PayloadAction<string>) => {
      const alert = state.alerts.find((a) => a.id === action.payload);
      if (alert) {
        alert.completed = true;
      }
    },
    updateAlertElapsedTime: (
      state,
      action: PayloadAction<{ id: string; elapsedTime: number }>
    ) => {
      const alert = state.alerts.find((a) => a.id === action.payload.id);
      if (alert) {
        alert.totalElapsedTime = action.payload.elapsedTime;
      }
    },
    clearAllAlerts: (state) => {
      state.alerts = state.alerts.map((a) => ({ ...a, completed: true }));
    },
  },
});

export const {
  addAlert,
  removeAlert,
  markAlertAsRead,
  markAlertAsCompleted,
  updateAlertElapsedTime,
  clearAllAlerts,
} = vacancyNotificationAlertsSlice.actions;

export default vacancyNotificationAlertsSlice.reducer;
